import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { Currency, useQuery } from '@sequencehq/api/utils'
import invariant from 'tiny-invariant'
import { useSelectedCurrency } from 'RevenueRecognition/view/utils/useSelectedCurrency'

type UseJournalDetailTotals = (props: { journalId: string }) =>
  | {
      data: {
        entries: Dashboardv99990101Api.GetJournal.GetJournalResponse['entries']
        totals: {
          debit: {
            amount: number
            ledgerAccounts: Array<Dashboardv99990101Api.GetJournal.LedgerAccountName>
          }
          credit: {
            amount: number
            ledgerAccounts: Array<Dashboardv99990101Api.GetJournal.LedgerAccountName>
          }
        }
        currency: Currency
      }
      isLoading: false
    }
  | {
      data: null
      isLoading: true
    }

export const useJournalDetailTotals: UseJournalDetailTotals = (props: {
  journalId: string
}) => {
  const selectedCurrency = useSelectedCurrency()
  const journalQuery = useQuery(
    dashboardv99990101Client.getJournal,
    { id: props.journalId },
    {
      select: journal => {
        if (!journal) {
          return null
        }

        return {
          entries: journal.entries,
          totals: {
            debit: journal.entries.reduce(
              (acc, entry) => {
                if (entry.direction === 'DEBIT') {
                  return {
                    amount: acc.amount + entry.amount,
                    ledgerAccounts: [...acc.ledgerAccounts, entry.account.name]
                  }
                }
                return acc
              },
              { amount: 0, ledgerAccounts: [] } as {
                amount: number
                ledgerAccounts: Array<Dashboardv99990101Api.GetJournal.LedgerAccountName>
              }
            ),
            credit: journal.entries.reduce(
              (acc, entry) => {
                if (entry.direction === 'CREDIT') {
                  return {
                    amount: acc.amount + entry.amount,
                    ledgerAccounts: [...acc.ledgerAccounts, entry.account.name]
                  }
                }
                return acc
              },
              { amount: 0, ledgerAccounts: [] } as {
                amount: number
                ledgerAccounts: Array<Dashboardv99990101Api.GetJournal.LedgerAccountName>
              }
            )
          }
        }
      }
    }
  )

  /**
   * We throw this error to be caught by the appropriate error boundary.
   */
  if (journalQuery.error) {
    throw new Error('Journal totals could not be loaded')
  }

  if (journalQuery.isPending || selectedCurrency.isLoading) {
    return {
      isLoading: true,
      data: null
    }
  }

  invariant(journalQuery.data, 'Data should be defined, if loaded')

  return {
    isLoading: false,
    data: {
      ...journalQuery.data,
      currency: selectedCurrency.currency
    }
  }
}
